import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { FiRepeat } from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  DetailTabBar, DetailRow, DetailStatusPill, ApproverCard, ImageBlock, FeedbackTab,
} from "../../components/financeDetail";
import { getTtkqByIdApi } from "../../api/finance.ttkq.api";
import { formatMoney } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const TEXT = "#08142D";
const MUTED = "#94A3B8";
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

const TtkqDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getTtkqByIdApi(id).then((d) => setData(d)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [id]);

  const tabs = useMemo(() => {
    const r = [{ key: "overview", title: "Tổng quan" }, { key: "transaction", title: "Giao dịch" }];
    if (data?._id) r.push({ key: "interactNote", title: "Phản hồi" });
    return r;
  }, [data]);
  useEffect(() => { if (tab >= tabs.length) setTab(0); }, [tabs.length, tab]);
  const activeKey = tabs[tab]?.key || "overview";

  const st = data?.status;
  const showHdmb = st === "APPROVED" && !data?.project?.is_allow_ttkq && !data?.statusSalesContract;

  const outlineBtn = { variant: "outline", borderColor: "#DCE1EA", color: TEXT, borderRadius: "8px", h: "48px", _hover: { bg: "#F7F9FC" } };

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : !data ? (
        <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy thông tin TTKQ</Text></Flex>
      ) : (
        <>
          <DetailTabBar tabs={tabs} index={tab} onChange={setTab} />

          <Box flex="1" overflowY="auto" px="16px" pt="16px">
            {activeKey === "overview" && (
              <Box pb="20px">
                <Text fontSize="16px" fontWeight={600} color={TEXT} noOfLines={2}>
                  {data?.project?.name || ""}{data?.project?.name ? " - " : ""}{data?.locking?.apartment?.code || "Không định danh"}
                </Text>
                <Box mt="12px"><DetailStatusPill status={st} /></Box>
                <ApproverCard approver={data?.approver} dateLabel={fmtD(data?.createdAt)} />

                <Box mt="8px">
                  <DetailRow label="Dự án" value={data?.project?.name} onPress={data?.projectId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an/${data.projectId}`) : undefined} />
                  {data?.code && <DetailRow label="Mã TTKQ" value={data.code} />}
                  <DetailRow label="Căn hộ" value={data?.locking?.apartment?.code ? `${data.locking.apartment.code} (${data?.locking?.apartment?.name || ""})` : "-"} />
                  <DetailRow label="Khách hàng" value={data?.customer?.name} onPress={data?.customerId ? () => navigate(`${EMP_BASE_PATH}/tai-chinh/khach-hang/${data.customerId}`) : undefined} />
                  <DetailRow label="Phương thức thanh toán" value={data?.paymentMethod?.name} />
                  <DetailRow label="Ngày ký TTKQ" value={fmtD(data?.time)} />
                  <DetailRow label="Ngày đóng tiền TTKQ" value={fmtD(data?.priceTime)} />
                  <DetailRow label="Ngày tạo TTKQ" value={fmtD(data?.createdAt)} />
                  <DetailRow label="Số tiền đóng TTKQ" value={formatMoney(data?.price || 0)} color={APP.primary200} />
                  <DetailRow label="Ghi chú" value={data?.note} last />
                </Box>
              </Box>
            )}

            {activeKey === "transaction" && (
              <Box pb="20px">
                <ImageBlock images={data?.delegationImage} emptyText="Chưa có ảnh UNC" />
                <Box h="16px" />
                <ImageBlock images={data?.priceSheetImage} emptyText="Chưa có ảnh phiếu tính giá" />
                <Box h="16px" />
                <ImageBlock images={data?.salesContractImages} emptyText="Chưa có ảnh TTKQ" />
              </Box>
            )}

            {activeKey === "interactNote" && (
              <Box pb="20px"><FeedbackTab query={{ lockingId: id }} /></Box>
            )}
          </Box>

          {tab === 0 && (
            <Box borderTop="1px solid #EEF1F6" p="16px">
              <Flex gap="16px">
                <Button flex="1" {...outlineBtn} leftIcon={<FiRepeat />} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/ttkq/chuyen/${id}`)}>Chuyển TTKQ</Button>
                {showHdmb && (
                  <Button flex="1" {...outlineBtn} onClick={() => navigate(`${EMP_BASE_PATH}/bao-tri`)}>Lên HDMB</Button>
                )}
              </Flex>
            </Box>
          )}
        </>
      )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Chi tiết đơn đăng ký TTKQ" right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      <AppBar title="Chi tiết đơn đăng ký TTKQ" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/ttkq`)} />
      {pageBody}
    </Box>
  );
};

export default TtkqDetail;
